import { DownloadRounded } from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { raFilterParams } from "../state";
import { saveFile, hasScope } from "../../utils";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import CustomLoader from "../../../../util/component/CustomLoader";
import { NetworkScope } from "../../types";
import { downloadNetworkRa } from "../../service/networkDownloadRa";
import getNetworkRaCount from "../../service/getRaCount";
import { IAgGridCustomToolbarActionsParams } from "../../../../util/component/AgGridCustomToolbar";
import { deepClone } from "../../../../util/state/commonFunctions";

interface ToolbarActionsProps extends IAgGridCustomToolbarActionsParams {
    raListRef: React.RefObject<any[]>;
}

const ToolbarActions = ({ raListRef, ...params }: ToolbarActionsProps) => {

    const { dispatchGlobalData, globalData } = useContext(GlobalContext);

    const { t } = useTranslation('network');

    const raFilter = useRecoilValue(raFilterParams);    

    const downloadNetworkRaMutation = useMutation(
        downloadNetworkRa,
        {
            onSuccess: (data) => {
                saveFile(data.file, data.fileName)
            },
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                })
            }
        }
    );

    const handleDownloadClick = useCallback(async () => {
        if((raListRef.current?.length ?? 0) <= 0){
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('export.raExport.error.select_ra_for_export'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
            return;
        }

        const visibleColDefs = (
            params.gridRef?.current?.columnApi
                .getAllDisplayedColumns()
                .map(c => c.getColDef())
                .filter(c => !c.suppressColumnsToolPanel || c.field === 'statusSubstatus') ?? []
        );

        var raFilterClone = deepClone(raFilter);
        raFilterClone.tenants = !!raFilterClone.tenants ? raFilterClone.tenants.map((elem: any) => elem.name) : null;
        raFilterClone.dealers = !!raFilterClone.dealers ? raFilterClone.dealers.map((elem: any) => elem.name) : null;

        const raCount = await getNetworkRaCount({
            filters: raFilterClone
        });

        if (raCount > 1000) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('export.raExport.error.download_max_rows'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
            return;
        }
        downloadNetworkRaMutation.mutate({
            filters: raFilterClone,
            fields: visibleColDefs.map(cd => ({
                field: cd.field ?? '',
                headerName: cd.headerName ?? ''
            }))
        });
    }, [raFilter]);

    if (!hasScope(globalData, NetworkScope.RaListExport)) {
        return <></>;
    }

    return (
        <Box px={2}>
            <Tooltip title={t('common.button.download')}>
                <IconButton
                    onClick={handleDownloadClick}
                    color="accent"
                >
                    <DownloadRounded />
                </IconButton>
            </Tooltip>
            <CustomLoader loading={downloadNetworkRaMutation.isLoading} />
        </Box>
    );
};

export default ToolbarActions;